import {LinkOutlined, ProductOutlined, StopOutlined} from '@ant-design/icons';
import type {TableProps} from 'antd';
import {Alert, Button, Descriptions, Flex, Image, Modal, Spin, Table, Tag, theme, Tooltip, Typography} from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import {Link} from 'react-router-dom';

import {ApiQuery} from '../../../../gen/api';
import {
    AdminAttentionProduct,
    AdminListingAnalytics,
    AdminMarketComparable,
    AdminMarketSnapshotStatus,
} from '../../../../gen/api/models';
import {marketplaceLabel} from '../../../helpers/status';
import {
    checkExpectations,
    formatAgo,
    formatPercent,
    formatPrice,
    formatShare,
    parseMoney,
    priceChangeSuggested,
    profit,
    REASON_LABEL,
    SEVERITY_DESCRIPTION,
    SEVERITY_LABEL,
    severityTone,
} from '../helpers/slowSelling';
import {FindProductLink} from './FindProductLink';
import {comparableColumns, empty, Hint, ListingCheckNote, TAG_COLOR} from './PriceAnalyticsParts';

const {Text, Title} = Typography;

export interface SlowSellingProductModalProps {
    item: AdminAttentionProduct;
    onClose: () => void;
    onResolve: (item: AdminAttentionProduct) => void;
}

type Token = ReturnType<typeof theme.useToken>['token'];

const listingColumns = (purchasePrice: number | string, token: Token): TableProps<AdminListingAnalytics>['columns'] => [
    {
        title: (
            <Hint
                label="Marketplace"
                hint="Where the listing is. Each marketplace has its own fees and shipping, so every number in the row is computed for it."
            />
        ),
        key: 'marketplace',
        width: 130,
        render: (_, l) => (
            <>
                {marketplaceLabel(l.marketplace)}
                <ListingCheckNote check={l.marketplaceCheck} />
            </>
        ),
    },
    {
        title: (
            <Hint
                label="Our price"
                hint="The listing price. If the buyer pays for shipping, that amount is shown after it; with free shipping we pay the label."
            />
        ),
        key: 'ourPrice',
        width: 120,
        render: (_, l) => (
            <>
                {formatPrice(l.ourPrice)}
                {parseMoney(l.buyerShipping) ? (
                    <Text
                        type="secondary"
                        style={{fontSize: 12}}
                    >
                        {' '}
                        + {formatPrice(l.buyerShipping)} ship
                    </Text>
                ) : null}
            </>
        ),
    },
    {
        title: (
            <Hint
                label="Payout"
                hint="What comes to us if it sells at our price: price + buyer shipping − marketplace fees (percent and fixed fee) − our shipping label."
            />
        ),
        dataIndex: 'ourNet',
        key: 'ourNet',
        width: 90,
        render: (value) => <Text type="secondary">{formatPrice(value)}</Text>,
    },
    {
        title: (
            <Hint
                label="Profit"
                hint="Payout minus the purchase price: what we earn on the item at our price."
            />
        ),
        key: 'profit',
        width: 90,
        render: (_, l) => {
            const value = profit(l.ourNet, purchasePrice);
            return value === undefined ? (
                empty
            ) : (
                <Text type={value < 0 ? 'danger' : undefined}>{formatPrice(value)}</Text>
            );
        },
    },
    {
        title: (
            <Hint
                label="Market"
                hint="What buyers pay for this model, shipping included: the median of similar eBay listings. * means there is no own data for this marketplace, so it is the eBay market times the marketplace ratio."
            />
        ),
        key: 'market',
        width: 110,
        render: (_, l) =>
            l.referencePrice === undefined || l.referencePrice === null ? (
                empty
            ) : (
                <Tooltip
                    title={
                        l.referenceSource === 'derived_from_ebay'
                            ? 'Derived from the eBay market'
                            : l.referenceSource === 'manual'
                              ? 'Entered manually'
                              : 'eBay comparables'
                    }
                >
                    {formatPrice(l.referencePrice)}
                    {l.referenceSource === 'derived_from_ebay' ? <Text type="secondary"> *</Text> : null}
                </Tooltip>
            ),
    },
    {
        title: (
            <Hint
                label="Suggested"
                hint="The price that sells: 5% below the market. It may be below Floor; then Min payout is more than this market gives. Hover a value to see the payout and profit at that price; red means a loss."
            />
        ),
        key: 'suggested',
        width: 110,
        render: (_, l) =>
            l.suggestedPrice === undefined || l.suggestedPrice === null ? (
                empty
            ) : (
                <Tooltip
                    title={`At this price: payout ${formatPrice(l.suggestedNet)}, profit ${formatPrice(profit(l.suggestedNet, purchasePrice))}`}
                >
                    <Text
                        strong={priceChangeSuggested(l.ourPrice, l.suggestedPrice)}
                        style={{
                            fontSize: 15,
                            color:
                                (profit(l.suggestedNet, purchasePrice) ?? 0) < 0
                                    ? token.colorError
                                    : priceChangeSuggested(l.ourPrice, l.suggestedPrice)
                                      ? token.colorPrimary
                                      : undefined,
                        }}
                    >
                        {formatPrice(l.suggestedPrice)}
                    </Text>
                </Tooltip>
            ),
    },
    {
        title: (
            <Hint
                label="Floor"
                hint="The lowest price whose payout reaches our minimum: the higher of purchase price + 20% and Min payout. It is our expectation, not a limit on Suggested: when Suggested is below Floor, the expectation is above the market."
            />
        ),
        dataIndex: 'floorPrice',
        key: 'floorPrice',
        width: 90,
        render: (value) => <Text type="secondary">{formatPrice(value)}</Text>,
    },
    {
        title: (
            <Hint
                label="Status"
                hint={
                    <>
                        The main problem (colored) and other conditions (grey). Above market: our price is above the
                        market. Not below market: not 5% below it. Min payout too high: selling at the market price
                        brings less than Min payout. No sales: listed 30+ days without a sale. Market dropped: the
                        market fell 5%+ since our last price change.
                    </>
                }
            />
        ),
        key: 'severity',
        width: 220,
        render: (_, l) => (
            <>
                <Tooltip title={SEVERITY_DESCRIPTION[l.severity]}>
                    <Tag color={TAG_COLOR[severityTone(l.severity)]}>{SEVERITY_LABEL[l.severity]}</Tag>
                </Tooltip>
                {l.reasons.length > 0 ? (
                    <Text
                        type="secondary"
                        style={{display: 'block', fontSize: 12}}
                    >
                        {l.reasons.map((reason) => REASON_LABEL[reason]).join(' · ')}
                    </Text>
                ) : null}
            </>
        ),
    },
    {
        title: (
            <Hint
                label="Market Δ"
                hint="How much the market moved since we last changed the price. Empty until there is market data from before that change."
            />
        ),
        dataIndex: 'marketChangePct',
        key: 'marketChangePct',
        width: 90,
        render: (value) =>
            value === undefined || value === null ? empty : <Text type="secondary">{formatPercent(value)}</Text>,
    },
    {
        title: (
            <Hint
                label="Listed"
                hint="Days since the listing was created and since its price last changed."
            />
        ),
        key: 'listed',
        width: 140,
        render: (_, l) => (
            <Text
                type="secondary"
                style={{fontSize: 12}}
            >
                {l.daysListed}d · changed {l.daysSincePriceChange}d ago
            </Text>
        ),
    },
    {
        title: '',
        dataIndex: 'link',
        key: 'link',
        width: 40,
        render: (link?: string) =>
            link ? (
                <Tooltip title="Open listing">
                    <a
                        href={link}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <LinkOutlined />
                    </a>
                </Tooltip>
            ) : null,
    },
];

export const SlowSellingProductModal: React.FC<SlowSellingProductModalProps> = ({item, onClose, onResolve}) => {
    const {token} = theme.useToken();
    const analyticsQuery = ApiQuery.useAdminProductAnalyticsQuery({id: item.productId}, {refetchOnWindowFocus: false});
    const analytics = analyticsQuery.data;
    const market = analytics?.ebayMarket;
    const listings = analytics?.listings ?? item.listings;
    const expectations = checkExpectations(listings, item.msrp, item.lowestResalePrice);

    return (
        <Modal
            open
            // Wide enough for the listings table (about 1215px) without scrolling; capped by the screen.
            width="min(1280px, calc(100vw - 32px))"
            onCancel={onClose}
            footer={
                <Flex
                    justify="end"
                    gap={8}
                >
                    <Link to={`/products/${item.productId}`}>
                        <Button icon={<ProductOutlined />}>Open product</Button>
                    </Link>
                    <Button
                        icon={<StopOutlined />}
                        onClick={() => onResolve(item)}
                    >
                        Resolve
                    </Button>
                    <Button
                        type="primary"
                        onClick={onClose}
                    >
                        Close
                    </Button>
                </Flex>
            }
        >
            <Flex
                align="flex-start"
                gap={16}
                wrap
                style={{marginBottom: 16}}
            >
                {item.thumbnailUrl ? (
                    <Image
                        src={item.thumbnailUrl}
                        alt={item.name}
                        width={72}
                        height={72}
                        preview={{src: item.imageUrl || item.thumbnailUrl, mask: null}}
                        style={{objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in'}}
                    />
                ) : null}
                <div style={{flex: 1, minWidth: 240}}>
                    <Title
                        level={5}
                        style={{marginTop: 0, marginBottom: 4}}
                    >
                        {item.name}{' '}
                        <Tooltip title="Open the product">
                            <Link to={`/products/${item.productId}`}>#{item.listingNumber}</Link>
                        </Tooltip>
                    </Title>
                    <div style={{marginTop: 8}}>
                        <Tag color={TAG_COLOR[severityTone(item.worstSeverity)]}>
                            {SEVERITY_LABEL[item.worstSeverity]}
                        </Tag>
                        <Text type="secondary">{SEVERITY_DESCRIPTION[item.worstSeverity]}</Text>
                    </div>
                </div>
                <Descriptions
                    size="small"
                    column={1}
                    style={{marginLeft: 'auto', minWidth: 220}}
                    items={[
                        {
                            key: 'purchase',
                            label: (
                                <Hint
                                    label="Purchase price"
                                    hint="What we paid. Floor keeps at least 20% over it after fees and shipping."
                                />
                            ),
                            children: formatPrice(item.purchasePrice),
                        },
                        {
                            key: 'msrp',
                            label: (
                                <Hint
                                    label="MSRP"
                                    hint="Manufacturer's retail price of a new item, for reference. It does not affect the calculation."
                                />
                            ),
                            children: (
                                <>
                                    {expectations.msrpMisleading ? (
                                        <Tooltip
                                            title={`The used market is ${formatPrice(expectations.marketPrice)}, only ${formatShare(expectations.marketToMsrp)} of MSRP. Pricing from MSRP overestimates this item.`}
                                        >
                                            <Text style={{color: token.colorWarningText}}>
                                                {formatPrice(item.msrp)}{' '}
                                                <Text
                                                    type="warning"
                                                    style={{fontSize: 12}}
                                                >
                                                    market is {formatShare(expectations.marketToMsrp)} of it
                                                </Text>
                                            </Text>
                                        </Tooltip>
                                    ) : (
                                        formatPrice(item.msrp)
                                    )}
                                    <FindProductLink
                                        asin={item.asin}
                                        name={item.name}
                                    />
                                </>
                            ),
                        },
                        {
                            key: 'minNet',
                            label: (
                                <Hint
                                    label="Min payout"
                                    hint="Lowest resale price: the least we want to get for the item after fees and shipping. E.g. bought for $15, min payout $25 means at least $10 profit. Floor is computed from it."
                                />
                            ),
                            children: !parseMoney(item.lowestResalePrice) ? (
                                '—'
                            ) : expectations.minPayoutTooHigh && expectations.bestPayout ? (
                                <Tooltip
                                    title={`Expectation too high: at market prices the best payout is ${formatPrice(expectations.bestPayout.value)} (${marketplaceLabel(expectations.bestPayout.marketplace)}). Lower Min payout or sell the item another way.`}
                                >
                                    <Text type="danger">
                                        {formatPrice(item.lowestResalePrice)}{' '}
                                        <span style={{fontSize: 12}}>
                                            market gives {formatPrice(expectations.bestPayout.value)}
                                        </span>
                                    </Text>
                                </Tooltip>
                            ) : (
                                formatPrice(item.lowestResalePrice)
                            ),
                        },
                    ]}
                />
            </Flex>

            <Text strong>Listings</Text>
            <Table<AdminListingAnalytics>
                style={{marginTop: 8}}
                size="small"
                rowKey={(l) => String(l.listingId)}
                columns={listingColumns(analytics?.purchasePrice ?? item.purchasePrice, token)}
                dataSource={listings}
                pagination={false}
                scroll={{x: 'max-content'}}
            />

            <Flex
                align="baseline"
                gap={8}
                style={{margin: '16px 0 8px'}}
            >
                <Text strong>eBay market</Text>
                {market ? (
                    <Tooltip title={dayjs(market.capturedAt).format('MM/DD/YYYY HH:mm')}>
                        <Text
                            type="secondary"
                            style={{fontSize: 12}}
                        >
                            updated {formatAgo(market.capturedAt)}
                        </Text>
                    </Tooltip>
                ) : null}
            </Flex>
            <Spin spinning={analyticsQuery.isLoading}>
                {analyticsQuery.isError ? (
                    <Alert
                        type="error"
                        message="Failed to load the market data"
                        showIcon
                    />
                ) : !market ? (
                    analyticsQuery.isLoading ? null : (
                        <Alert
                            type="info"
                            message="No market data yet: the worker has not searched eBay for this product."
                            showIcon
                        />
                    )
                ) : market.status !== AdminMarketSnapshotStatus.MarketSnapshotStatusOk ? (
                    <Alert
                        type="warning"
                        showIcon
                        message={
                            market.status === AdminMarketSnapshotStatus.MarketSnapshotStatusNoData
                                ? `Too few comparables found on eBay (${market.comparablesCount}).`
                                : `eBay search failed: ${market.error ?? market.status}`
                        }
                    />
                ) : (
                    <>
                        <Descriptions
                            size="small"
                            column={{xs: 2, md: 4}}
                            items={[
                                {
                                    key: 'median',
                                    label: (
                                        <Hint
                                            label="Median"
                                            hint="The middle total of the comparables: half are cheaper, half more expensive. This is the eBay market price."
                                        />
                                    ),
                                    children: (
                                        <Text
                                            strong
                                            style={{color: token.colorText, fontSize: 18}}
                                        >
                                            {formatPrice(market.marketMedian)}
                                        </Text>
                                    ),
                                },
                                {
                                    key: 'low',
                                    label: (
                                        <Hint
                                            label="Low"
                                            hint="The cheapest comparable after outliers are dropped."
                                        />
                                    ),
                                    children: formatPrice(market.marketLow),
                                },
                                {
                                    key: 'high',
                                    label: (
                                        <Hint
                                            label="High"
                                            hint="The most expensive comparable after outliers are dropped."
                                        />
                                    ),
                                    children: formatPrice(market.marketHigh),
                                },
                                {
                                    key: 'count',
                                    label: (
                                        <Hint
                                            label="Comparables"
                                            hint="How many listings the market is based on, after filtering. The table shows up to 20 closest to the median."
                                        />
                                    ),
                                    children: market.comparablesCount,
                                },
                            ]}
                        />
                        <Table<AdminMarketComparable>
                            size="small"
                            rowKey={(c) => c.itemId}
                            columns={comparableColumns}
                            dataSource={market.comparables}
                            pagination={false}
                            scroll={{x: 'max-content', y: 320}}
                        />
                    </>
                )}
            </Spin>
        </Modal>
    );
};
